import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AuthProvider } from './store/AuthContext';
import ProtectedRoute from './routes/ProtectedRoute';

const MainLayout = lazy(() => import('./layouts/MainLayout'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const FileList = lazy(() => import('./pages/FileList'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const BanRecords = lazy(() => import('./pages/BanRecords'));
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const SFTPUserList = lazy(() => import('./pages/SFTPUserList'));
const ShareList = lazy(() => import('./pages/ShareList'));
const ShareDownload = lazy(() => import('./pages/ShareDownload'));
const MobileUpload = lazy(() => import('./pages/MobileUpload'));
const SessionAttachments = lazy(() => import('./pages/SessionAttachments'));
const SessionDetail = lazy(() => import('./pages/SessionDetail'));
const SubscriptionManage = lazy(() => import('./pages/SubscriptionManage'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const RemotePortal = lazy(() => import('./pages/RemotePortal'));

const loading = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
);

function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={loading}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/share/:shareCode" element={<ShareDownload />} />
              <Route path="/m/:token" element={<MobileUpload />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="files" element={<FileList />} />
                <Route path="shares" element={<ShareList />} />
                <Route path="session-attachments" element={<SessionAttachments />} />
                <Route path="session-attachments/:sessionId" element={<SessionDetail />} />
                <Route path="subscriptions" element={<SubscriptionManage />} />
                <Route path="subscriptions-manage" element={<Navigate to="/subscriptions" replace />} />
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<Profile />} />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="remote-portal" element={<RemotePortal />} />
                <Route 
                  path="users" 
                  element={
                    <ProtectedRoute requiresAdmin>
                      <UserManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="audit-logs" 
                  element={
                    <ProtectedRoute requiresAdmin>
                      <AuditLogs />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="sftp-users" 
                  element={
                    <ProtectedRoute requiresAdmin>
                      <SFTPUserList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="security" 
                  element={
                    <ProtectedRoute requiresAdmin>
                      <BanRecords />
                    </ProtectedRoute>
                  } 
                />
              </Route>

              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/files" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
